import React, { useState, useMemo } from 'react';
import { Download, FileText, Clock, History } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExportConfigPanel, ExportConfig } from '@/components/exports/ExportConfigPanel';
import { ReportTemplates } from '@/components/exports/ReportTemplates';
import { ScheduledReports } from '@/components/exports/ScheduledReports';
import { ExportPreviewModal } from '@/components/exports/ExportPreviewModal';
import { QuickExportWidget } from '@/components/exports/QuickExportWidget';
import { ExportService } from '@/services/exportService';
import { generateMockLeads } from '@/services/mockLeads';
import { toast } from '@/hooks/useToast';

const availableColumns = [
  { key: 'id', label: 'Submission ID', type: 'string' as const },
  { key: 'submitted_at', label: 'Timestamp', type: 'date' as const },
  { key: 'first_name', label: 'First Name', type: 'string' as const },
  { key: 'last_name', label: 'Last Name', type: 'string' as const },
  { key: 'email', label: 'Email', type: 'string' as const },
  { key: 'phone', label: 'Phone', type: 'string' as const },
  { key: 'ip_address', label: 'IP Address', type: 'string' as const },
  { key: 'zip_code', label: 'Zip Code', type: 'string' as const },
  { key: 'risk_score', label: 'Risk Score', type: 'percentage' as const },
  { key: 'detected_patterns', label: 'Fraud Patterns', type: 'string' as const },
  { key: 'status', label: 'Status', type: 'string' as const },
  { key: 'source', label: 'Source', type: 'string' as const },
  { key: 'saved_amount', label: 'Amount Saved', type: 'currency' as const }
];

export function ExportReports() {
  const [showPreview, setShowPreview] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<ExportConfig | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);

  // Mock data - in real app, this would come from API
  const allLeads = generateMockLeads(100);

  const todayStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayLeads = allLeads.filter(lead => {
      const leadDate = new Date(lead.timestamp);
      leadDate.setHours(0, 0, 0, 0);
      return leadDate.getTime() === today.getTime();
    });
    
    return {
      total: todayLeads.length,
      fraudulent: todayLeads.filter(lead => lead.riskScore >= 70).length
    };
  }, [allLeads]);

  const weekStats = useMemo(() => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const weekLeads = allLeads.filter(lead => new Date(lead.timestamp) >= weekAgo);
    
    return {
      total: weekLeads.length,
      fraudulent: weekLeads.filter(lead => lead.riskScore >= 70).length
    };
  }, [allLeads]);

  const recentExports = [
    {
      filename: 'fraud-leads-2025-01-31.csv',
      size: '2.3 MB',
      date: new Date(Date.now() - 60 * 60 * 1000),
      url: '#'
    },
    {
      filename: 'weekly-report-2025-01-30.pdf',
      size: '1.8 MB', 
      date: new Date(Date.now() - 24 * 60 * 60 * 1000),
      url: '#'
    },
    {
      filename: 'all-leads-2025-01-29.xlsx',
      size: '5.1 MB',
      date: new Date(Date.now() - 48 * 60 * 60 * 1000),
      url: '#'
    }
  ];

  const handlePreview = (config: ExportConfig) => {
    // Filter data based on config
    let filteredData = allLeads;
    
    // Apply filters (simplified for demo)
    if (config.dataTypes.includes('fraudulent')) {
      filteredData = filteredData.filter(lead => lead.riskScore >= 70);
    }
    
    setPreviewConfig(config);
    setPreviewData(filteredData);
    setShowPreview(true);
  };

  const handleExport = (config: ExportConfig) => {
    try {
      // Filter data based on config
      let filteredData = allLeads;
      
      if (config.dataTypes.includes('fraudulent')) {
        filteredData = filteredData.filter(lead => lead.riskScore >= 70);
      }

      const columns = availableColumns.filter(col => config.columns.includes(col.key));
      
      const exportOptions = {
        filename: config.filename,
        columns,
        data: filteredData,
        title: 'Fraud Detection Report',
        dateRange: config.dateRange.from && config.dateRange.to 
          ? `${config.dateRange.from.toLocaleDateString()} - ${config.dateRange.to.toLocaleDateString()}`
          : undefined
      };

      switch (config.format) {
        case 'csv':
          ExportService.exportToCSV(exportOptions);
          break;
        case 'json':
          ExportService.exportToJSON(exportOptions);
          break;
        case 'excel':
          ExportService.exportToExcel(exportOptions);
          break;
        case 'pdf':
          ExportService.exportToPDF(exportOptions);
          break;
      }

      toast(`Your ${config.format.toUpperCase()} export is being generated.`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('There was an error generating your export.');
    }
  };

  const handleEmailReport = (config: ExportConfig) => {
    toast('Email configuration dialog would open here.');
  };

  const handleQuickExport = (period: 'today' | 'week', type: 'fraudulent' | 'all') => {
    let filteredData = allLeads;
    let filename = '';
    
    if (period === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filteredData = allLeads.filter(lead => {
        const leadDate = new Date(lead.timestamp);
        leadDate.setHours(0, 0, 0, 0);
        return leadDate.getTime() === today.getTime();
      });
      filename = `today-${type}-leads`;
    } else {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      filteredData = allLeads.filter(lead => new Date(lead.timestamp) >= weekAgo);
      filename = `week-${type}-leads`;
    }
    
    if (type === 'fraudulent') {
      filteredData = filteredData.filter(lead => lead.riskScore >= 70);
    }

    const exportOptions = {
      filename,
      columns: availableColumns.slice(0, 8), // First 8 columns for quick export
      data: filteredData
    };

    ExportService.exportToCSV(exportOptions);
    
    toast(`Quick Export Started: Exporting ${filteredData.length} leads to CSV.`);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Export & Reports</h1>
          <p className="text-muted-foreground">
            Generate reports, schedule exports, and download lead data
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Quick Export Widget */}
        <div className="lg:col-span-1">
          <QuickExportWidget
            onExportToday={(type) => handleQuickExport('today', type)}
            onExportWeek={(type) => handleQuickExport('week', type)}
            onCustomExport={() => {}} // Switch to Export tab
            todayStats={todayStats}
            weekStats={weekStats}
            recentExports={recentExports}
          />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Tabs defaultValue="export" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="export" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="scheduled" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Scheduled 
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="export" className="mt-6">
              <ExportConfigPanel
                data={allLeads}
                onPreview={handlePreview}
                onExport={handleExport}
                onEmailReport={handleEmailReport}
              />
            </TabsContent>

            <TabsContent value="templates" className="mt-6">
              <ReportTemplates
                templates={[]}
                onUseTemplate={(template) => toast(`Using ${template.name} template configuration.`)}
                onEditTemplate={(template) => toast(`Editing ${template.name} template.`)}
                onScheduleTemplate={(template) => toast(`Scheduling ${template.name} template.`)}
                onCreateTemplate={(template) => toast(`Created new template: ${template.name}.`)}
              />
            </TabsContent>

            <TabsContent value="scheduled" className="mt-6">
              <ScheduledReports
                reports={[]}
                onEdit={(id) => toast('Edit schedule dialog would open here.')}
                onDelete={(id) => toast('Scheduled report has been deleted.')}
                onToggle={(id, active) => toast(`Scheduled report is now ${active ? 'active' : 'inactive'}.`)}
                onRunNow={(id) => toast('Report generation started manually.')}
                onCreate={(report) => toast(`Created new scheduled report: ${report.name}.`)}
              />
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Export History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Export history will be displayed here</p>
                    <p className="text-sm">Track all your exports and downloads</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Export Preview Modal */}
      {showPreview && previewConfig && (
        <ExportPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          config={previewConfig}
          data={previewData}
          onAdjustColumns={() => {
            setShowPreview(false);
            toast('Column adjustment would open here.');
          }}
          onChangeFormat={() => {
            setShowPreview(false);
            toast('Format selection would open here.');
          }}
          onProceedExport={() => {
            setShowPreview(false);
            handleExport(previewConfig);
          }}
        />
      )}
    </div>
  );
}